import { createContext, useContext } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { useIsHydrated } from '~/utils/useIsHydrated'

const FadeInStaggerContext = createContext(false)

// Use % so the intersection root stays valid when the viewport is very short (high zoom).
const viewport = { once: true, margin: '0px 0px -25%' }

export function FadeIn(props: React.ComponentPropsWithoutRef<typeof motion.div>) {
  const isHydrated = useIsHydrated()
  const shouldReduceMotion = useReducedMotion()
  let isInStaggerGroup = useContext(FadeInStaggerContext)

  return (
    <motion.div
      initial={isHydrated && !shouldReduceMotion ? { opacity: 0 } : { opacity: 1 }}
      variants={
        shouldReduceMotion
          ? {
              hidden: { opacity: 1 },
              visible: { opacity: 1 },
            }
          : {
              hidden: { opacity: 0 },
              visible: { opacity: 1 },
            }
      }
      transition={{ duration: shouldReduceMotion ? 0 : 0.5 }}
      {...(isInStaggerGroup
        ? {}
        : shouldReduceMotion
        ? {
            initial: 'visible',
            whileInView: 'visible',
            viewport,
          }
        : {
            initial: 'hidden',
            whileInView: 'visible',
            viewport,
          })}
      {...props}
    />
  )
}

export function FadeInWithOffset(props: React.ComponentPropsWithoutRef<typeof motion.div>) {
  const isHydrated = useIsHydrated()
  let shouldReduceMotion = useReducedMotion()
  let isInStaggerGroup = useContext(FadeInStaggerContext)

  return (
    <motion.div
      initial={isHydrated && !shouldReduceMotion ? { opacity: 0 } : { opacity: 1 }}
      variants={{
        hidden: { opacity: shouldReduceMotion ? 1 : 0, y: shouldReduceMotion ? 0 : 24 },
        visible: { opacity: 1, y: 0 },
      }}
      transition={{ duration: shouldReduceMotion ? 0 : 0.5 }}
      {...(isInStaggerGroup
        ? {}
        : shouldReduceMotion
        ? {
            initial: 'visible',
            whileInView: 'visible',
            viewport,
          }
        : {
            initial: 'hidden',
            whileInView: 'visible',
            viewport,
          })}
      {...props}
    />
  )
}

export function FadeInStagger({
  duration = 'fast',
  ...props
}: React.ComponentPropsWithoutRef<typeof motion.div> & {
  duration?: 'regular' | 'fast' | 'fastest'
}) {
  const isHydrated = useIsHydrated()
  const shouldReduceMotion = useReducedMotion()

  return (
    <FadeInStaggerContext.Provider value={true}>
      <motion.div
        initial={isHydrated && !shouldReduceMotion ? 'hidden' : 'visible'}
        whileInView="visible"
        viewport={viewport}
        transition={{
          staggerChildren: shouldReduceMotion
            ? 0
            : duration === 'fast'
            ? 0.12
            : duration === 'fastest'
            ? 0.05
            : 0.2,
        }}
        {...props}
      />
    </FadeInStaggerContext.Provider>
  )
}
